import type { ComponentPropsWithoutRef, CSSProperties, FC } from "react";
import { useEffect, useRef, useState } from "react";

import classNames from "classnames";

import styles from "./TierPointsProgressBar.module.scss";

export type TiersNames = {
  silver?: string;
  gold?: string;
  black?: string;
};

export type TierPointsProgressBarProps = {
  currentPoints: number;
  pointsForBlackTier?: number;
  tier: string;
  pointsForGoldTier?: number;
  pointsLabel?: string | number;
  pointsTitle?: string;
  pointsFooter?: string;
  tiersNames?: TiersNames;
};

type TierPointsLabelProps = {
  tierName?: string;
  value?: string | number;
} & ComponentPropsWithoutRef<"p">;

const TierPointsLabel: FC<TierPointsLabelProps> = ({ tierName, value, className, ...props }) => (
  <p className={classNames(styles.label, className)} {...props}>
    <span className={styles.labelTierName}>{tierName}</span>
    <span className={styles.labelValue}>{value}</span>
  </p>
);

export const TierPointsProgressBar: FC<TierPointsProgressBarProps> = ({
  currentPoints,
  pointsForBlackTier,
  tier,
  pointsForGoldTier,
  pointsLabel = "0",
  pointsTitle,
  pointsFooter,
  tiersNames = {},
}) => {
  const getPositionInPercent = (maxPoints: number, valueToCheck: number) => {
    const progress = parseFloat(((valueToCheck / maxPoints) * 100).toFixed(0));

    return progress >= 100 ? 100 : progress;
  };

  const [withPointsOutside, setWithPointsOutside] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const pointsRef = useRef<HTMLDivElement>(null);

  const maxTierPoints = Math.max(pointsForGoldTier || 0, pointsForBlackTier || 0);

  const currentProgress = getPositionInPercent(maxTierPoints, currentPoints);
  const pointsOnProgressBar = currentProgress === 100 ? `${maxTierPoints}+` : currentPoints;

  const userTier = tier.toLowerCase();
  const progressBarClasses = classNames(styles.progressBar, {
    [styles.silver]: userTier === "silver",
    [styles.gold]: userTier === "gold",
    [styles.black]: userTier === "black",
  });

  useEffect(() => {
    const progressBar = progressBarRef.current;
    const progressText = pointsRef.current;
    if (!progressBar || !progressText) return;

    const progressBarWidth = progressBar.offsetWidth;
    const progressTextWidth = progressText.offsetWidth;

    if (progressTextWidth + 20 > progressBarWidth * (currentPoints / maxTierPoints)) {
      setWithPointsOutside(true);
    } else {
      setWithPointsOutside(false);
    }
  }, [currentPoints, maxTierPoints]);

  if (pointsForGoldTier === undefined && pointsForBlackTier === undefined) {
    return null;
  }

  const shouldShowGoldTier = pointsForGoldTier !== undefined && pointsForGoldTier !== pointsForBlackTier;

  return (
    <div className={styles.tierPointsProgressBar}>
      <p className={styles.pointsTitle}>{pointsTitle}</p>
      <div className={styles.pointsWrapper}>
        <div
          ref={progressBarRef}
          data-testid="progress-bar"
          className={progressBarClasses}
          style={
            {
              "--progress": `${currentProgress}%`,
            } as CSSProperties
          }
        >
          <p
            ref={pointsRef}
            className={classNames(styles.pointsInside, {
              [styles.fullProgress]: currentProgress === 100,
              [styles.pointsOutside]: withPointsOutside,
              [styles.whiteText]: userTier === "black",
              [styles.blackText]: (userTier === "black" && withPointsOutside) || userTier !== "black",
            })}
          >
            {`${pointsOnProgressBar}`}
          </p>
        </div>
        <div className={styles.pointsLabels}>
          <TierPointsLabel tierName={tiersNames.silver} value={pointsLabel} className={styles.firstLabel} />
          <div
            className={styles.goldTierLabelWrapper}
            style={{
              insetInlineStart: `${
                pointsForBlackTier !== undefined && pointsForGoldTier !== undefined
                  ? getPositionInPercent(pointsForBlackTier, pointsForGoldTier)
                  : 0
              }%`,
            }}
          >
            {shouldShowGoldTier && <TierPointsLabel tierName={tiersNames.gold} value={pointsForGoldTier} />}
          </div>
          <TierPointsLabel
            tierName={tiersNames.black}
            value={pointsForBlackTier !== undefined ? pointsForBlackTier : maxTierPoints}
            className={styles.lastLabel}
          />
        </div>
        <p className={styles.pointsFooter}>{pointsFooter}</p>
      </div>
    </div>
  );
};
